/* eslint-disable no-console */
import TagManager from 'react-gtm-module-nonce';
import { DataLayerClient } from '../client/api-google-analytics';
import {
  payServiceClient,
  PaymentInformation,
  SubscriptionInformation,
  InstalmentPlanInformation,
  StripeInstalmentPlanFormData,
} from '../client/base';
import { nonceTemplate } from '../nonce';
import { syncWithAnalyticsConsent } from '../utils/oneTrustConsent';

const clientAppDataLayer: DataLayer[] = [];
let isTagManagerInitialized = false;
let isAnalyticsTrackingEnabled = false;
let isConsentSyncStarted = false;
let isInitialSyncComplete = false;
let shouldSendPageView = false;
let pendingCorrelationId: string | undefined;
let gtmCodePromise: Promise<string | null> | undefined;

export class GoogleAnalyticsHelpers {
  static InitializeTagManagerAsync = async () => {
    await GoogleAnalyticsHelpers.ensureConsentAwareInitialization();
  };

  static InitializeTagManagerGaAndSendPageViewAsync = async (correlationId?: string) => {
    shouldSendPageView = true;
    pendingCorrelationId = GoogleAnalyticsHelpers.persistCorrelationId(correlationId);

    await GoogleAnalyticsHelpers.ensureConsentAwareInitialization();
  };

  static getCookie(name: string): string {
    const nameLenPlus = name.length + 1;
    return (
      document.cookie
        .split(';')
        .map((c) => c.trim())
        .filter((cookie) => {
          return cookie.startsWith(`${name}=`);
        })
        .map((cookie) => {
          return decodeURIComponent(cookie.substring(nameLenPlus));
        })[0] || ''
    );
  }

  static getClientAppDataLayer() {
    return clientAppDataLayer;
  }

  static UpdateDataLayerForPayment = (paymentInformation?: PaymentInformation | null) => {
    if (!paymentInformation) {
      return;
    }
    const dataLayer = {
      countryCode: paymentInformation.countryCode,
      operationCode: paymentInformation.operationCode,
      applicationCode: paymentInformation.operationCode,
      providerConfiguration: paymentInformation.providerConfigurationCode,
      correlationId: paymentInformation.correlationId,
      amount: paymentInformation.amount,
      currency: paymentInformation.currency,
      paymentId: paymentInformation.paymentId,
      paymentMethodGroup: paymentInformation.paymentMethodGroup,
    };

    GoogleAnalyticsHelpers.UpdateDataLayer(dataLayer);
  };

  static UpdateDataLayerForSubscription = (subscriptionInformation: SubscriptionInformation) => {
    const dataLayer = {
      countryCode: subscriptionInformation.countryCode,
      operationCode: subscriptionInformation.operationCode,
      providerConfiguration: subscriptionInformation.providerConfigurationCode,
      correlationId: subscriptionInformation.correlationId,
      amount: subscriptionInformation.amount,
      currency: subscriptionInformation.currency,
      subscriptionId: subscriptionInformation.subscriptionId,
      paymentMethodGroup: subscriptionInformation.paymentMethodGroup,
      planId: subscriptionInformation.planId,
      productName: subscriptionInformation.productName,
      productDescription: subscriptionInformation.productDescription,
      intervalUnit: subscriptionInformation.intervalUnit,
      intervalCount: subscriptionInformation.intervalCount,
      trialIntervalUnit: subscriptionInformation.trialIntervalUnit,
      trialIntervalCount: subscriptionInformation.trialIntervalCount,
    };

    GoogleAnalyticsHelpers.UpdateDataLayer(dataLayer);
  };

  static UpdateDataLayerForInstalmentPlan = (
    instalmentPlan: InstalmentPlanInformation,
    transaction: StripeInstalmentPlanFormData
  ) => {
    const dataLayer = {
      countryCode: instalmentPlan.countryCode,
      operationCode: instalmentPlan.operationCode,
      providerConfiguration: instalmentPlan.providerConfigurationCode,
      correlationId: instalmentPlan.correlationId,
      amount: transaction.firstInstalmentAmount,
      currency: instalmentPlan.currency,
      instalmentPlanId: instalmentPlan.instalmentPlanId,
      paymentMethodGroup: instalmentPlan.paymentMethodGroup,
    };

    GoogleAnalyticsHelpers.UpdateDataLayer(dataLayer);
  };

  static UpdateDataLayerWithStripeCardPaymentFlow = (
    stripeCardPaymentFlow: 'Card' | 'Payment Request Button'
  ) => {
    GoogleAnalyticsHelpers.UpdateDataLayer({ stripeCardPaymentFlow: stripeCardPaymentFlow });
  };

  static UpdateDataLayer = (event: any) => {
    if (!isAnalyticsTrackingEnabled || !isTagManagerInitialized) {
      return;
    }

    const dataLayerEntry =
      event.category && event.action
        ? { ...event, event: `${event.category} - ${event.action}` }
        : event;

    clientAppDataLayer.push({ event: JSON.stringify(event) });
    TagManager.dataLayer({
      dataLayer: dataLayerEntry,
      dataLayerName: 'dataLayer',
    });
  };

  static RecordGenericPaymentFail = () => {
    GoogleAnalyticsHelpers.UpdateDataLayer({
      category: 'Payment',
      action: 'Payment Attempt Failed',
      label: 'Generic',
    });
  };

  private static readonly ensureConsentAwareInitialization = async () => {
    if (!isConsentSyncStarted) {
      isConsentSyncStarted = true;

      syncWithAnalyticsConsent(
        () => {
          isAnalyticsTrackingEnabled = true;
          // Only kick off initialization from the callback for late consent grants
          // (i.e. after the initial sync call has already returned). For the synchronous
          // grant path the `if (isAnalyticsTrackingEnabled)` block below handles it,
          // preventing a double call to initializeTagManagerAndPendingPageView.
          if (isInitialSyncComplete) {
            void GoogleAnalyticsHelpers.initializeTagManagerAndPendingPageView();
          }
        },
        () => {
          isAnalyticsTrackingEnabled = false;
        }
      );
      isInitialSyncComplete = true;
    }

    if (isAnalyticsTrackingEnabled) {
      await GoogleAnalyticsHelpers.initializeTagManagerAndPendingPageView();
    }
  };

  private static readonly initializeTagManagerAndPendingPageView = async () => {
    const gtmCode = await GoogleAnalyticsHelpers.getGtmCode();
    if (!gtmCode) {
      return;
    }

    if (!isTagManagerInitialized) {
      TagManager.initialize({
        gtmId: gtmCode,
        nonce: nonceTemplate,
      });
      isTagManagerInitialized = true;
    }

    if (!shouldSendPageView) {
      return;
    }

    await GoogleAnalyticsHelpers.restorePersistedDataLayer(pendingCorrelationId);

    GoogleAnalyticsHelpers.UpdateDataLayer({
      event: 'pageview',
      path: globalThis.location.pathname,
    });

    shouldSendPageView = false;
  };

  private static readonly getGtmCode = async () => {
    gtmCodePromise ??= payServiceClient
      .getGoogleAnalyticsSettings()
      .then((response) => response.data.gtmCode)
      .catch((err) => {
        console.error('GoogleAnalyticsSettingsError', err.message);
        return null;
      });

    return gtmCodePromise;
  };

  private static readonly persistCorrelationId = (correlationId?: string) => {
    if (correlationId) {
      localStorage.setItem('correlationId', correlationId);
      return correlationId;
    }

    return localStorage.getItem('correlationId') || undefined;
  };

  private static readonly restorePersistedDataLayer = async (correlationId?: string) => {
    const clientId = GoogleAnalyticsHelpers.getCookie('_ga');
    if (!clientId) {
      return;
    }

    try {
      const response = await new DataLayerClient().get(clientId);
      if (!response.data.dataLayer) {
        return;
      }

      const dataLayer = JSON.parse(response.data.dataLayer);
      clientAppDataLayer.push(...dataLayer);

      const dataLayerCorrelationId = clientAppDataLayer.find((x) =>
        x.event.includes('dataLayerCorrelationId')
      );

      if (
        dataLayerCorrelationId &&
        JSON.parse(dataLayerCorrelationId.event).dataLayerCorrelationId === correlationId
      ) {
        for (let i = 0; i < dataLayer.length; i++) {
          TagManager.dataLayer({
            dataLayer: JSON.parse(dataLayer[i].event),
            dataLayerName: 'dataLayer',
          });
        }
      }
    } catch (err: any) {
      console.error('GoogleAnalyticsDataLayerError', err?.message ?? err);
    }
  };
}

interface DataLayer {
  event: string;
}

// Exported for test isolation only — resets module-level state between test cases.
export const resetGoogleAnalyticsForTests = () => {
  clientAppDataLayer.length = 0;
  isTagManagerInitialized = false;
  isAnalyticsTrackingEnabled = false;
  isConsentSyncStarted = false;
  isInitialSyncComplete = false;
  shouldSendPageView = false;
  pendingCorrelationId = undefined;
  gtmCodePromise = undefined;
  localStorage.removeItem('correlationId');
};
